import { withoutBlankFilterChoices } from "@/components/non_registrant/dashboard-column-filter-match";
import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import { PartyDetailsSheet } from "@/components/non_registrant/party-details-sheet";
import { PartyUploadSheet } from "@/components/non_registrant/party-upload-sheet";
import { Button } from "@/components/ui/button";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import {
  getPartiesOptions,
  getQuestionOptionsOptions,
  updatePartyMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { PartyResponseSchema } from "@/hera-api/client/types.gen";
import type { HelpPortalId } from "@/lib/help-portal";
import { omtAmsImpersonatePortalHref } from "@/lib/omt-ams-impersonate";
import {
  formatQuestionColumnValue,
  getQueryErrorHeading,
  getQueryErrorMessage,
} from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Plus, RotateCcw, Trash2, Upload } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";

/** Party row with optional questions summary for the list. */
type PartyRow = PartyResponseSchema & {
  _questionsSummary?: string;
};

/** Actions column widths sized to their buttons so no dead space trails them. */
const ACTIONS_WIDTH_WITH_IMPERSONATE = 168;
const ACTIONS_WIDTH_ICON_ONLY = 72;

const PARTY_ACCESSOR_KEYS = [
  "id",
  "name",
  "company_name",
  "email",
  "phone",
  "address1",
  "address2",
  "city",
  "state",
  "zipcode",
  "active",
  "exempt",
  "questions",
] as const;

type PartyAccessorKeys = (typeof PARTY_ACCESSOR_KEYS)[number];
type PartyColumnProperties = ColumnProperties<PartyRow, PartyAccessorKeys>;

const PARTY_COLUMNS_WITHOUT_BLANK_FILTER = new Set<string>([
  "id",
  "active",
  "exempt",
]);

const buildPartyColumnProperties = (
  accessorKey: PartyAccessorKeys,
): PartyColumnProperties => {
  switch (accessorKey) {
    case "id":
      return {
        displayName: "ID",
        defaultVisible: false,
        getValue: (row, _key) => row.id,
      };
    case "name":
      return {
        displayName: "Name",
        defaultVisible: true,
        getValue: (row, _key) => row.name ?? "",
      };
    case "company_name":
      return {
        displayName: "Company Name",
        defaultVisible: true,
        getValue: (row, _key) => row.company_name ?? "",
      };
    case "email":
      return {
        displayName: "Email",
        defaultVisible: true,
        getValue: (row, _key) => row.email ?? "",
      };
    case "phone":
      return {
        displayName: "Phone",
        defaultVisible: true,
        getValue: (row, _key) => row.phone ?? "",
      };
    case "address1":
      return {
        displayName: "Address 1",
        defaultVisible: true,
        getValue: (row, _key) => row.address1 ?? "",
      };
    case "address2":
      return {
        displayName: "Address 2",
        defaultVisible: true,
        getValue: (row, _key) => row.address2 ?? "",
      };
    case "city":
      return {
        displayName: "City",
        defaultVisible: true,
        getValue: (row, _key) => row.city ?? "",
      };
    case "state":
      return {
        displayName: "State",
        defaultVisible: true,
        getValue: (row, _key) => row.state ?? "",
      };
    case "zipcode":
      return {
        displayName: "Zipcode",
        defaultVisible: true,
        getValue: (row, _key) => row.zipcode ?? "",
      };
    case "active":
      return {
        displayName: "Active",
        defaultVisible: true,
        getValue: (row, _key) => (row.deleted_at == null ? "Yes" : "No"),
      };
    case "exempt":
      return {
        displayName: "Exempt",
        defaultVisible: true,
        getValue: (row, _key) => (row.is_exempt ? "Yes" : "No"),
      };
    case "questions":
      return {
        displayName: "Questions",
        defaultVisible: true,
        getValue: (row, _key) => row._questionsSummary ?? "",
      };
    default: {
      const _exhaust: never = accessorKey;
      void _exhaust;
      return {
        displayName: String(accessorKey),
        defaultVisible: false,
        getValue: (_row, _key) => "",
      };
    }
  }
};

const getPartyColumnProperties = (
  accessorKey: PartyAccessorKeys,
): PartyColumnProperties =>
  withoutBlankFilterChoices(
    accessorKey,
    PARTY_COLUMNS_WITHOUT_BLANK_FILTER,
    buildPartyColumnProperties(accessorKey),
  );

interface DashboardPartiesProps {
  isAuthSetupComplete: boolean;
  /** Government users can view parties but cannot create, edit, or delete them. */
  canWriteParties?: boolean;
  /** Operator portal: show Actions with OMT/AMS portal impersonation. */
  showImpersonateButton?: boolean;
  /** Page chrome rendered inside the list scroller so it scrolls with the rows. */
  leadingContent?: ReactNode;
  /** Help Center the Exempt switch opens. */
  helpPortal?: HelpPortalId;
}

export function DashboardParties({
  isAuthSetupComplete,
  canWriteParties = true,
  showImpersonateButton = false,
  leadingContent,
  helpPortal = "operator",
}: DashboardPartiesProps) {
  const queryClient = useQueryClient();
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({
    active: ["Yes"],
  });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedNacId, setSelectedNacId] = useState<string | null>(null);
  const [uploadSheetOpen, setUploadSheetOpen] = useState(false);

  const {
    data: partiesData,
    isLoading: isPartiesLoading,
    error: partiesError,
  } = useQuery({
    ...getPartiesOptions({
      query: { include_deleted: true },
    }),
    enabled: isAuthSetupComplete,
  });

  const { data: allOptionsData } = useQuery({
    ...getQuestionOptionsOptions({ query: { include_deleted: false } }),
    enabled: isAuthSetupComplete,
  });

  const optionsByPartyId = useMemo(() => {
    const map = new Map<string, { question: unknown }[]>();
    for (const opt of allOptionsData?.question_options ?? []) {
      const list = map.get(opt.party_id) ?? [];
      list.push({
        question: opt.question_summary?.question ?? undefined,
      });
      map.set(opt.party_id, list);
    }
    return map;
  }, [allOptionsData]);

  const partiesForTable: PartyRow[] = useMemo(() => {
    const list = partiesData?.parties ?? [];
    return list.map((p) => {
      const opts = optionsByPartyId.get(p.id) ?? [];
      const summary =
        opts.length === 0
          ? ""
          : opts
              .map((o) => formatQuestionColumnValue(o.question))
              .filter(Boolean)
              .join(", ");
      return { ...p, _questionsSummary: summary };
    });
  }, [partiesData?.parties, optionsByPartyId]);

  const updateMutation = useMutation({
    ...updatePartyMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getParties"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const handleDelete = useCallback(
    (e: React.MouseEvent, nacId: string) => {
      e.stopPropagation();
      updateMutation.mutate({
        path: { party_id: nacId },
        body: { is_deleted: true },
      });
    },
    [updateMutation],
  );

  const handleRestore = useCallback(
    (e: React.MouseEvent, nacId: string) => {
      e.stopPropagation();
      updateMutation.mutate({
        path: { party_id: nacId },
        body: { is_deleted: false },
      });
    },
    [updateMutation],
  );

  const openPartySheet = useCallback((nacId: string | null) => {
    setSelectedNacId(nacId);
    setSheetOpen(true);
  }, []);

  if (isPartiesLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col min-w-0">
        {leadingContent}
        <div className="flex justify-center items-center py-8">
          <LoadingScreen message="Fetching parties..." />
        </div>
      </div>
    );
  }
  if (partiesError) {
    return (
      <div className="flex min-h-0 flex-1 flex-col min-w-0">
        {leadingContent}
        <ErrorComponent
          title={getQueryErrorHeading(partiesError)}
          message={getQueryErrorMessage(partiesError)}
        />
      </div>
    );
  }

  const listChrome = (
    <>
      {leadingContent}
      {canWriteParties ? (
        <div className="flex justify-end items-center gap-2 mb-2">
          <Button
            type="button"
            onClick={() => setUploadSheetOpen(true)}
            aria-label="Upload Parties"
          >
            <Upload className="h-4 w-4 mr-1" />
            Upload Parties
          </Button>
          <Button
            type="button"
            onClick={() => openPartySheet(null)}
            aria-label="Add party"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Party
          </Button>
        </div>
      ) : null}
    </>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 min-w-0 overflow-hidden">
      <PartyDetailsSheet
        isOpen={sheetOpen}
        onOpenChange={setSheetOpen}
        nacId={selectedNacId}
        readOnly={!canWriteParties}
        showImpersonateButton={showImpersonateButton}
        helpPortal={helpPortal}
      />

      {canWriteParties ? (
        <PartyUploadSheet
          isOpen={uploadSheetOpen}
          onOpenChange={setUploadSheetOpen}
        />
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DashboardTable<PartyRow, PartyAccessorKeys>
          leadingContent={listChrome}
          lockToViewport
          data={partiesForTable}
          accessorKeys={PARTY_ACCESSOR_KEYS}
          getColumnProperties={getPartyColumnProperties}
          getRowId={(row) => row.id}
          tablePersistenceKey="non-registrant-parties"
          initialSortColumn="name"
          initialSortDirection="asc"
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          onRowClick={(rowId) => openPartySheet(rowId)}
          rowActionsColumnWidth={
            showImpersonateButton
              ? ACTIONS_WIDTH_WITH_IMPERSONATE
              : ACTIONS_WIDTH_ICON_ONLY
          }
          renderRowActions={
            canWriteParties || showImpersonateButton
              ? (row: PartyRow) => {
                  const impersonateEmail = row.email?.trim() ?? "";
                  return (
                    <div className="flex items-center gap-1">
                      {showImpersonateButton && impersonateEmail ? (
                        <Button
                          size="xs"
                          variant="outline"
                          className="shrink-0"
                          aria-label="Impersonate party"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              omtAmsImpersonatePortalHref(impersonateEmail),
                              "_blank",
                            );
                          }}
                        >
                          <ExternalLink className="h-3 w-3 mr-1.5" />
                          Impersonate
                        </Button>
                      ) : null}
                      {canWriteParties ? (
                        row.deleted_at == null ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleDelete(e, row.id)}
                            disabled={updateMutation.isPending}
                            aria-label="Delete party"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleRestore(e, row.id)}
                            disabled={updateMutation.isPending}
                            aria-label="Restore party"
                            title="Restore"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )
                      ) : null}
                    </div>
                  );
                }
              : undefined
          }
        />
      </div>
    </div>
  );
}
